import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatCurrencyFull, formatNumber, formatMonth } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart as RechartsPie,
    Pie,
    Cell,
} from 'recharts';
import {
    BarChart3,
    Calendar,
    Download,
    FileSpreadsheet,
    Layers,
    Loader2,
    Music2,
    Sparkles,
    TrendingUp,
    Trophy,
    Zap,
} from 'lucide-react';
import api from '../../lib/api';
import InternalReportsPage from '../admin/InternalReportsPage';

const COLORS = ['#7C3AED', '#00FF9D', '#CCFF00', '#00F2EA', '#FFB700', '#FF4444', '#A78BFA', '#34D399'];

const getCurrentMonth = () => new Date().toISOString().slice(0, 7);

const shiftMonth = (yyyyMm, delta) => {
    const [year, month] = yyyyMm.split('-').map(Number);
    const date = new Date(year, month - 1 + delta, 1);
    return date.toISOString().slice(0, 7);
};

const getPresetRange = (preset) => {
    const end = getCurrentMonth();
    if (preset === '3m') return { start: shiftMonth(end, -2), end };
    if (preset === '6m') return { start: shiftMonth(end, -5), end };
    if (preset === '12m') return { start: shiftMonth(end, -11), end };
    return { start: '', end: '' };
};

const compactInr = (value = 0) => {
    const numeric = Number(value) || 0;
    const abs = Math.abs(numeric);
    if (abs >= 10000000) return `₹${(numeric / 10000000).toFixed(abs >= 100000000 ? 0 : 1)}Cr`;
    if (abs >= 100000) return `₹${(numeric / 100000).toFixed(abs >= 1000000 ? 0 : 1)}L`;
    if (abs >= 1000) return `₹${(numeric / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
    return `₹${numeric.toFixed(0)}`;
};

const safeCurrency = (value, currency = 'INR') => {
    try {
        return formatCurrency(value || 0, currency);
    } catch {
        return currency === 'INR' ? compactInr(value || 0) : `$${Number(value || 0).toLocaleString()}`;
    }
};

const getPercentChange = (current, previous) => {
    const now = Number(current) || 0;
    const prev = Number(previous) || 0;
    if (!prev) return 0;
    return ((now - prev) / prev) * 100;
};

const getTrackGrowth = (track) => {
    const raw = track.growth ?? track.growth_percent ?? track.change_percent ?? track.mom_growth ?? track.revenue_growth;
    if (raw === undefined || raw === null || raw === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

const ReportsPage = () => {
    const { user, isInternal } = useAuth();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [downloadingReport, setDownloadingReport] = useState(null);
    const [report, setReport] = useState(null);
    const [activeTab, setActiveTab] = useState('monthly');
    const [viewMode, setViewMode] = useState('transaction_month');
    const [preset, setPreset] = useState('6m');
    const [range, setRange] = useState(() => getPresetRange('6m'));

    useEffect(() => {
        if (preset !== 'custom') setRange(getPresetRange(preset));
    }, [preset]);

    useEffect(() => {
        if (isInternal) {
            setLoading(false);
            return;
        }

        const fetchReport = async () => {
            if (!user?.label_id) return;
            const isFirstLoad = !report;
            if (isFirstLoad) {
                setLoading(true);
            } else {
                setRefreshing(true);
            }
            try {
                const params = new URLSearchParams({ month_type: viewMode });
                if (range.start) params.append('start_date', range.start);
                if (range.end) params.append('end_date', range.end);
                const response = await api.get(`/revenue/label-report/${user.label_id}?${params.toString()}`);
                setReport(response.data);
            } catch (error) {
                console.error('Failed to fetch label report:', error);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        };

        fetchReport();
    }, [isInternal, user?.label_id, viewMode, range.start, range.end]);

    const summary = report?.summary || {};
    const label = report?.label || {};

    const monthlyData = useMemo(() => {
        return [...(report?.by_month || [])]
            .filter((m) => m?.month)
            .sort((a, b) => String(a.month).localeCompare(String(b.month)))
            .map((m) => ({
                ...m,
                monthLabel: formatMonth(m.month),
                net_inr: Number(m.net_inr || m.revenue_inr || m.net_revenue_inr || 0),
                gross_inr: Number(m.gross_inr || m.gross_revenue_inr || 0),
                units: Number(m.units || m.streams || m.sale_units || 0),
                track_count: Number(m.track_count || m.tracks || 0),
            }));
    }, [report]);

    const platformData = useMemo(() => {
        return [...(report?.by_platform || report?.platform_breakdown || [])]
            .map((p) => ({
                platform: p.platform || p._id || 'Unknown',
                net_inr: Number(p.net_inr || p.revenue_inr || p.revenue || 0),
                gross_inr: Number(p.gross_inr || 0),
                streams: Number(p.streams || p.units || 0),
            }))
            .sort((a, b) => b.net_inr - a.net_inr);
    }, [report]);

    const topTracks = useMemo(() => {
        return [...(report?.top_tracks || [])]
            .map((t) => ({
                ...t,
                title: t.title || t._id || 'Untitled',
                artist: t.artist || 'Unknown',
                net_inr: Number(t.net_inr || t.revenue_inr || t.revenue || 0),
                gross_inr: Number(t.gross_inr || 0),
                units: Number(t.units || t.streams || 0),
                growth: getTrackGrowth(t),
            }))
            .sort((a, b) => b.net_inr - a.net_inr);
    }, [report]);

    const topArtists = useMemo(() => {
        return [...(report?.top_artists || [])]
            .map((a) => ({
                ...a,
                artist: a.artist || 'Unknown',
                net_inr: Number(a.net_inr || a.revenue_inr || a.revenue || 0),
                units: Number(a.units || a.streams || 0),
                track_count: Number(a.track_count || 0),
            }))
            .sort((a, b) => b.net_inr - a.net_inr);
    }, [report]);

    const periodInsight = useMemo(() => {
        const first = monthlyData[0];
        const last = monthlyData[monthlyData.length - 1];
        const growth = first && last ? getPercentChange(last.net_inr, first.net_inr) : 0;
        const topPlatform = platformData[0];
        const topTrack = topTracks[0];
        const bestMonth = monthlyData.reduce((best, current) => (current.net_inr > (best?.net_inr || 0) ? current : best), null);
        const totalNet = monthlyData.reduce((sum, item) => sum + Number(item.net_inr || 0), 0);
        const platformShare = topPlatform && totalNet > 0 ? ((topPlatform.net_inr / totalNet) * 100).toFixed(0) : null;
        const trackShare = topTrack && totalNet > 0 ? ((topTrack.net_inr / totalNet) * 100).toFixed(0) : null;

        return {
            growth,
            topPlatform,
            topTrack,
            bestMonth,
            platformShare,
            trackShare,
            totalNet,
        };
    }, [monthlyData, platformData, topTracks]);

    const movers = useMemo(() => {
        const withGrowth = topTracks.filter((track) => track.growth !== null);
        if (withGrowth.length) {
            return {
                gainers: [...withGrowth].sort((a, b) => b.growth - a.growth).slice(0, 3),
                decliners: [...withGrowth].sort((a, b) => a.growth - b.growth).slice(0, 3),
                mode: 'growth',
            };
        }
        return {
            gainers: topTracks.slice(0, 3),
            decliners: topTracks.slice(3, 6),
            mode: 'contributors',
        };
    }, [topTracks]);

    const handleDownload = async (suffix = 'complete', customRange = range) => {
        setDownloadingReport(suffix);
        try {
            const params = new URLSearchParams({
                month_type: viewMode,
                report_type: suffix,
            });
            if (user?.label_id) params.append('label_id', user.label_id);
            if (customRange?.start) params.append('start_date', customRange.start);
            if (customRange?.end) params.append('end_date', customRange.end);

            const response = await api.get(`/revenue/export?${params.toString()}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            const mode = viewMode === 'reporting_month' ? 'cashflow' : 'performance';
            const period = `${customRange?.start || 'all'}_to_${customRange?.end || 'all'}`;
            const contentType = response.headers?.['content-type'] || '';
            const extension = contentType.includes('text/csv') ? 'csv' : 'xlsx';
            link.href = url;
            link.setAttribute('download', `elara_${mode}_${suffix}_${period}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);

            let message = 'Export failed. Please try again.';

            try {
                if (error.response?.data instanceof Blob) {
                    const text = await error.response.data.text();
                    const payload = JSON.parse(text);

                    if (typeof payload?.detail === 'string') {
                        message = payload.detail;
                    } else if (payload?.detail?.message) {
                        message = payload.detail.message;
                    }
                } else if (error.response?.data?.detail?.message) {
                    message = error.response.data.detail.message;
                } else if (typeof error.response?.data?.detail === 'string') {
                    message = error.response.data.detail;
                }
            } catch (parseError) {
                console.error('Failed to parse export error:', parseError);
            }

            window.alert(message);
        } finally {
            setDownloadingReport(null);
        }
    };

    const handleMonthDownload = (month) => {
        if (!month) return;
        handleDownload(`monthly_statement_${month}`, { start: month, end: month });
    };

    if (isInternal) return <InternalReportsPage />;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/15 via-card to-secondary/10 p-6">
                <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-primary/20 blur-3xl" />
                <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                            <Sparkles className="h-3.5 w-3.5" /> Statement Center
                        </div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight">Reports & Statements</h1>
                        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                            Monthly statements, custom-period reports, revenue charts, track performance, platform earnings and instant downloads.
                        </p>
                    </div>
                    <div className="flex flex-col items-start gap-2 lg:items-end">
                        <Button onClick={() => handleDownload('full_report')} disabled={Boolean(downloadingReport)} className="shadow-lg shadow-primary/20">
                            {downloadingReport === 'full_report' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                            Export Full Report
                        </Button>
                        <p className="max-w-sm text-xs text-muted-foreground lg:text-right">
                            Detailed downloads are available for up to 3 months at a time. Large datasets are automatically provided as CSV.
                        </p>
                    </div>
                </div>
            </div>

            <Card className="border-border/40 bg-card/70 backdrop-blur">
                <CardContent className="p-4">
                    <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
                        <div className="space-y-3">
                            <div className="flex flex-wrap gap-2">
                                {[["3m", "Last 3 Months"], ["6m", "Last 6 Months"], ["12m", "Last 12 Months"], ["custom", "Custom Range"]].map(([value, labelText]) => (
                                    <Button
                                        key={value}
                                        variant={preset === value ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setPreset(value)}
                                    >
                                        {labelText}
                                    </Button>
                                ))}
                            </div>
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/50 px-3 py-2">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">From</span>
                                    <input
                                        type="month"
                                        value={range.start}
                                        onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, start: e.target.value })); }}
                                        className="bg-transparent text-sm outline-none"
                                    />
                                </div>
                                <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/50 px-3 py-2">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">To</span>
                                    <input
                                        type="month"
                                        value={range.end}
                                        onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, end: e.target.value })); }}
                                        className="bg-transparent text-sm outline-none"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <div className="flex rounded-lg bg-muted/40 p-1">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('transaction_month')}
                                    className={`rounded-md px-3 py-2 text-sm font-medium transition ${viewMode === 'transaction_month' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    Performance
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('reporting_month')}
                                    className={`rounded-md px-3 py-2 text-sm font-medium transition ${viewMode === 'reporting_month' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    Cash Flow
                                </button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {refreshing && (
                <div className="flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating report for selected period...
                </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <MetricCard title="Net Payable" value={safeCurrency(summary.net_payable_inr || summary.revenue_inr || 0, 'INR')} icon={TrendingUp} highlight />
                <MetricCard title="Gross Revenue" value={safeCurrency(summary.gross_revenue_inr || 0, 'INR')} subtitle="Before label share" icon={BarChart3} />
                <MetricCard title="Streams" value={formatNumber(summary.total_streams || summary.streams || 0)} icon={Music2} />
                <MetricCard title="Tracks Earned" value={formatNumber(summary.total_tracks || topTracks.length || 0)} icon={Layers} />
            </div>

            <Card className="border-primary/20 bg-gradient-to-r from-primary/10 to-secondary/10">
                <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h3 className="font-heading text-lg font-bold">{label.name || 'Your Label'}</h3>
                        <p className="text-sm text-muted-foreground">
                            {viewMode === 'reporting_month' ? 'Cash Flow' : 'Performance'} report for {range.start || 'all'} to {range.end || 'all'} · Label share {label.revenue_share_percentage || summary.label_share_percentage || 70}%
                        </p>
                    </div>
                    <Badge className="w-fit bg-primary/20 text-primary">Net = Gross × Label Share</Badge>
                </CardContent>
            </Card>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid h-auto grid-cols-2 gap-2 bg-muted/30 p-2 md:grid-cols-5">
                    <TabsTrigger value="monthly">Statements</TabsTrigger>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="tracks">Tracks</TabsTrigger>
                    <TabsTrigger value="platforms">Platforms</TabsTrigger>
                    <TabsTrigger value="artists">Artists</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                    <MonthlyStatementCards
                        monthlyData={monthlyData.slice(-4).reverse()}
                        downloadingReport={downloadingReport}
                        onMonthDownload={handleMonthDownload}
                    />

                    <div className="grid gap-6 xl:grid-cols-[2fr_1fr]">
                        <Card className="border-border/40 bg-card/60">
                            <CardHeader>
                                <CardTitle>Monthly Net Revenue</CardTitle>
                                <CardDescription>Selected period, grouped by {viewMode === 'reporting_month' ? 'reporting month' : 'transaction month'}.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[360px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={monthlyData} margin={{ left: 8, right: 12, top: 8, bottom: 8 }}>
                                            <defs>
                                                <linearGradient id="elaraNetRevenue" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.35} />
                                                    <stop offset="95%" stopColor="#7C3AED" stopOpacity={0.02} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                            <XAxis dataKey="monthLabel" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                                            <YAxis tickFormatter={compactInr} tick={{ fill: 'hsl(var(--muted-foreground))' }} width={68} />
                                            <Tooltip content={<ChartTooltip label="Net Revenue" />} />
                                            <Area type="monotone" dataKey="net_inr" name="Net Revenue" stroke="#7C3AED" strokeWidth={3} fill="url(#elaraNetRevenue)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-border/40 bg-card/60">
                            <CardHeader>
                                <CardTitle>Download Center</CardTitle>
                                <CardDescription>Export reports for the selected period.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <DownloadRow title="Complete Statement Pack" description="Monthly, track, artist and platform data" onClick={() => handleDownload('complete_statement_pack')} downloading={downloadingReport === 'complete_statement_pack'} />
                                <DownloadRow title="Track Performance" description="Track-wise revenue and streams" onClick={() => handleDownload('track_performance')} downloading={downloadingReport === 'track_performance'} />
                                <DownloadRow title="Platform Revenue" description="Platform-wise revenue report" onClick={() => handleDownload('platform_revenue')} downloading={downloadingReport === 'platform_revenue'} />
                                <DownloadRow title="Artist Revenue" description="Artist-wise revenue summary" onClick={() => handleDownload('artist_revenue')} downloading={downloadingReport === 'artist_revenue'} />
                            </CardContent>
                        </Card>
                    </div>

                    <ElaraInsight insight={periodInsight} monthlyData={monthlyData} />

                    <div className="grid gap-6 xl:grid-cols-2">
                        <BiggestMovers movers={movers} />
                        <Card className="border-border/40 bg-card/60">
                            <CardHeader>
                                <CardTitle>Top Contributors</CardTitle>
                                <CardDescription>Tracks and platforms driving this report period.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Top tracks</p>
                                    {topTracks.slice(0, 4).map((track, index) => (
                                        <MiniRank key={`${track.title}-${index}`} rank={index + 1} title={track.title} meta={track.artist} value={safeCurrency(track.net_inr, 'INR')} />
                                    ))}
                                </div>
                                <div className="space-y-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Top platforms</p>
                                    {platformData.slice(0, 4).map((platform, index) => (
                                        <MiniRank key={platform.platform} rank={index + 1} title={platform.platform} meta={`${formatNumber(platform.streams)} streams`} value={safeCurrency(platform.net_inr, 'INR')} />
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="monthly" className="space-y-6">
                    <MonthlyStatementsExperience
                        monthlyData={monthlyData}
                        downloadingReport={downloadingReport}
                        onMonthDownload={handleMonthDownload}
                        onDownloadPeriod={() => handleDownload('monthly_statements')}
                    />
                </TabsContent>

                <TabsContent value="tracks" className="space-y-6">
                    <BiggestMovers movers={movers} />
                    <Card className="border-border/40 bg-card/60">
                        <CardHeader>
                            <CardTitle>Top Tracks</CardTitle>
                            <CardDescription>Track-wise performance for the selected period.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <DataTable>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>#</TableHead>
                                        <TableHead>Track</TableHead>
                                        <TableHead>Artist</TableHead>
                                        <TableHead>ISRC</TableHead>
                                        <TableHead className="text-right">Streams</TableHead>
                                        <TableHead className="text-right">Net Payable</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {topTracks.map((track, i) => (
                                        <TableRow key={`${track.title}-${i}`}>
                                            <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                                            <TableCell className="max-w-[280px] truncate font-medium" title={track.title}>{track.title}</TableCell>
                                            <TableCell className="text-muted-foreground">{track.artist}</TableCell>
                                            <TableCell className="font-mono text-xs text-muted-foreground">{track.isrc || '-'}</TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(track.units)}</TableCell>
                                            <TableCell className="text-right font-mono text-secondary">{safeCurrency(track.net_inr, 'INR')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </DataTable>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="platforms" className="space-y-6">
                    <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
                        <Card className="border-border/40 bg-card/60">
                            <CardHeader>
                                <CardTitle>Platform Revenue Mix</CardTitle>
                                <CardDescription>Net payable split by platform.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-[320px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <RechartsPie>
                                            <Pie data={platformData} dataKey="net_inr" nameKey="platform" cx="50%" cy="50%" innerRadius={58} outerRadius={108} paddingAngle={4}>
                                                {platformData.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip formatter={(value) => formatCurrencyFull(value, 'INR')} />
                                        </RechartsPie>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-border/40 bg-card/60">
                            <CardHeader>
                                <CardTitle>Platform Details</CardTitle>
                                <CardDescription>Sortable view by revenue contribution.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {platformData.map((p, i) => (
                                        <div key={p.platform} className="flex items-center justify-between rounded-xl border border-border/40 bg-muted/20 p-3">
                                            <div className="flex items-center gap-3">
                                                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                                <span className="font-medium">{p.platform}</span>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-mono font-semibold text-secondary">{safeCurrency(p.net_inr, 'INR')}</p>
                                                <p className="text-xs text-muted-foreground">{formatNumber(p.streams)} streams</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="artists" className="space-y-6">
                    <Card className="border-border/40 bg-card/60">
                        <CardHeader>
                            <CardTitle>Top Artists</CardTitle>
                            <CardDescription>Artist-wise revenue and streams.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <DataTable>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>#</TableHead>
                                        <TableHead>Artist</TableHead>
                                        <TableHead className="text-right">Tracks</TableHead>
                                        <TableHead className="text-right">Streams</TableHead>
                                        <TableHead className="text-right">Net Payable</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {topArtists.map((artist, i) => (
                                        <TableRow key={`${artist.artist}-${i}`}>
                                            <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                                            <TableCell className="font-medium">{artist.artist}</TableCell>
                                            <TableCell className="text-right">{artist.track_count}</TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(artist.units)}</TableCell>
                                            <TableCell className="text-right font-mono text-secondary">{safeCurrency(artist.net_inr, 'INR')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </DataTable>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

const MetricCard = ({ title, value, subtitle, icon: Icon, highlight = false }) => (
    <Card className={highlight ? 'border-secondary/30 bg-gradient-to-br from-secondary/15 to-secondary/5' : 'border-border/40 bg-card/60'}>
        <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-sm text-muted-foreground">{title}</p>
                    <p className={`mt-2 text-2xl font-heading font-bold ${highlight ? 'text-secondary' : ''}`}>{value}</p>
                    {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
                </div>
                <div className="rounded-xl bg-primary/10 p-2 text-primary">
                    <Icon className="h-5 w-5" />
                </div>
            </div>
        </CardContent>
    </Card>
);

const ElaraInsight = ({ insight, monthlyData }) => {
    const growth = insight.growth || 0;
    const direction = growth >= 0 ? 'increased' : 'decreased';
    const growthText = monthlyData.length > 1 ? `Revenue ${direction} ${Math.abs(growth).toFixed(1)}% from ${monthlyData[0]?.monthLabel} to ${monthlyData[monthlyData.length - 1]?.monthLabel}.` : 'Select a wider period to see revenue movement.';
    const platformText = insight.topPlatform ? `${insight.topPlatform.platform} contributed ${insight.platformShare || 0}% of revenue in this period.` : 'Platform contribution will appear after revenue data is available.';
    const trackText = insight.topTrack ? `${insight.topTrack.title} was the top earning track${insight.trackShare ? ` with ${insight.trackShare}% contribution` : ''}.` : 'Top track insight will appear after track data is available.';
    const monthText = insight.bestMonth ? `${insight.bestMonth.monthLabel} was the strongest month at ${safeCurrency(insight.bestMonth.net_inr, 'INR')}.` : 'Best month will appear once monthly data is available.';

    return (
        <Card className="border-primary/20 bg-gradient-to-br from-primary/10 via-card to-secondary/10">
            <CardContent className="p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex items-start gap-3">
                        <div className="rounded-2xl bg-primary/15 p-3 text-primary"><Sparkles className="h-5 w-5" /></div>
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-primary">ELARA Insight</p>
                            <h3 className="mt-1 font-heading text-xl font-bold">What changed this period?</h3>
                            <div className="mt-3 grid gap-2 text-sm text-muted-foreground md:grid-cols-2">
                                <InsightLine text={growthText} />
                                <InsightLine text={platformText} />
                                <InsightLine text={trackText} />
                                <InsightLine text={monthText} />
                            </div>
                        </div>
                    </div>
                    <Badge className="w-fit bg-secondary/20 text-secondary">Smart Summary</Badge>
                </div>
            </CardContent>
        </Card>
    );
};

const InsightLine = ({ text }) => (
    <div className="flex gap-2 rounded-xl border border-border/40 bg-background/40 p-3">
        <Zap className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>{text}</span>
    </div>
);

const MonthlyStatementsExperience = ({ monthlyData, downloadingReport, onMonthDownload, onDownloadPeriod }) => {
    const [archiveYear, setArchiveYear] = useState('all');
    const ordered = [...(monthlyData || [])].sort((a, b) => String(b.month).localeCompare(String(a.month)));
    const recentStatements = ordered.slice(0, 12);
    const years = [...new Set(ordered.map((m) => String(m.month || '').slice(0, 4)).filter(Boolean))].sort((a, b) => b.localeCompare(a));
    const archiveData = ordered.filter((m) => archiveYear === 'all' || String(m.month || '').startsWith(archiveYear));
    const totalRevenue = ordered.reduce((sum, m) => sum + Number(m.net_inr || 0), 0);
    const bestMonth = ordered.reduce((best, m) => (Number(m.net_inr || 0) > Number(best?.net_inr || 0) ? m : best), null);

    return (
        <div className="space-y-6">
            <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/15 via-card to-secondary/10">
                <CardContent className="p-5">
                    <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr] lg:items-center">
                        <div>
                            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                                <FileSpreadsheet className="h-3.5 w-3.5" /> Statement Library
                            </div>
                            <h2 className="font-heading text-2xl font-bold">Monthly Statements</h2>
                            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                                Recent statements are shown as premium cards. Older history stays searchable in the archive below, built for years of historical reporting.
                            </p>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                            <div className="rounded-2xl border border-border/40 bg-background/50 p-4">
                                <p className="text-xs text-muted-foreground">Statements</p>
                                <p className="mt-1 font-heading text-2xl font-bold">{ordered.length}</p>
                            </div>
                            <div className="rounded-2xl border border-border/40 bg-background/50 p-4">
                                <p className="text-xs text-muted-foreground">Total net</p>
                                <p className="mt-1 font-heading text-2xl font-bold text-secondary">{safeCurrency(totalRevenue, 'INR')}</p>
                            </div>
                            <div className="rounded-2xl border border-border/40 bg-background/50 p-4">
                                <p className="text-xs text-muted-foreground">Best month</p>
                                <p className="mt-1 font-heading text-lg font-bold">{bestMonth ? formatMonth(bestMonth.month) : '-'}</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-border/40 bg-card/60">
                <CardHeader>
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                            <CardTitle>Recent Statements</CardTitle>
                            <CardDescription>Last 12 available months, shown first for quick access. Download any month as a statement.</CardDescription>
                        </div>
                        <Button
                            variant="outline"
                            onClick={onDownloadPeriod}
                            disabled={downloadingReport === 'monthly_statements'}
                        >
                            {downloadingReport === 'monthly_statements'
                                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                : <Download className="mr-2 h-4 w-4" />}
                            Export Selected Period
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {recentStatements.length ? (
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                            {recentStatements.map((m) => (
                                <StatementCard
                                    key={m.month}
                                    statement={m}
                                    downloading={downloadingReport === `monthly_statement_${m.month}`}
                                    onMonthDownload={onMonthDownload}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="rounded-xl border border-border/40 bg-muted/20 p-4 text-sm text-muted-foreground">No statements available for this period.</p>
                    )}
                </CardContent>
            </Card>

            <Card className="border-border/40 bg-card/60">
                <CardHeader>
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                            <CardTitle>Statement Archive</CardTitle>
                            <CardDescription>For historical data ingestion and older reporting periods.</CardDescription>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/50 px-3 py-2">
                            <span className="text-sm text-muted-foreground">Year</span>
                            <select
                                value={archiveYear}
                                onChange={(e) => setArchiveYear(e.target.value)}
                                className="bg-transparent text-sm outline-none"
                            >
                                <option value="all">All years</option>
                                {years.map((year) => <option key={year} value={year}>{year}</option>)}
                            </select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <DataTable>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Month</TableHead>
                                <TableHead className="text-right">Net Payable</TableHead>
                                <TableHead className="text-right">Gross</TableHead>
                                <TableHead className="text-right">Streams</TableHead>
                                <TableHead className="text-right">Tracks</TableHead>
                                <TableHead className="text-right">Statement</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {archiveData.map((m) => (
                                <TableRow key={m.month}>
                                    <TableCell className="font-medium">{formatMonth(m.month)}</TableCell>
                                    <TableCell className="text-right font-mono text-secondary">{safeCurrency(m.net_inr, 'INR')}</TableCell>
                                    <TableCell className="text-right font-mono">{safeCurrency(m.gross_inr, 'INR')}</TableCell>
                                    <TableCell className="text-right font-mono">{formatNumber(m.units)}</TableCell>
                                    <TableCell className="text-right">{m.track_count}</TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => onMonthDownload(m.month)}
                                            disabled={downloadingReport === `monthly_statement_${m.month}`}
                                            className="h-8"
                                        >
                                            {downloadingReport === `monthly_statement_${m.month}`
                                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                : <Download className="h-3.5 w-3.5" />}
                                            <span className="ml-2 hidden sm:inline">Download</span>
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </DataTable>
                </CardContent>
            </Card>
        </div>
    );
};

const StatementCard = ({ statement, downloading, onMonthDownload }) => (
    <div className="group relative overflow-hidden rounded-2xl border border-border/40 bg-gradient-to-br from-muted/40 to-background/70 p-4 transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10">
        <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-primary/10 blur-2xl transition group-hover:bg-primary/20" />
        <div className="relative">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{formatMonth(statement.month)}</p>
                    <p className="mt-2 text-2xl font-heading font-bold text-secondary">{safeCurrency(statement.net_inr, 'INR')}</p>
                    <p className="mt-1 text-xs text-muted-foreground">Net payable statement</p>
                </div>
                <div className="rounded-xl bg-primary/10 p-2 text-primary">
                    <FileSpreadsheet className="h-5 w-5" />
                </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-lg bg-background/60 p-2">
                    <p className="text-muted-foreground">Gross</p>
                    <p className="font-mono font-medium">{compactInr(statement.gross_inr)}</p>
                </div>
                <div className="rounded-lg bg-background/60 p-2">
                    <p className="text-muted-foreground">Streams</p>
                    <p className="font-mono font-medium">{formatNumber(statement.units)}</p>
                </div>
                <div className="rounded-lg bg-background/60 p-2">
                    <p className="text-muted-foreground">Tracks</p>
                    <p className="font-mono font-medium">{statement.track_count}</p>
                </div>
            </div>
            <div className="mt-4 flex gap-2">
                <Button variant="default" size="sm" className="flex-1" onClick={() => onMonthDownload(statement.month)} disabled={downloading}>
                    {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                    Statement
                </Button>
            </div>
        </div>
    </div>
);

const MonthlyStatementCards = ({ monthlyData, downloadingReport, onMonthDownload }) => (
    <Card className="border-border/40 bg-card/60">
        <CardHeader>
            <CardTitle>Monthly Statements</CardTitle>
            <CardDescription>Statement cards with one-click monthly downloads.</CardDescription>
        </CardHeader>
        <CardContent>
            {monthlyData?.length ? (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {monthlyData.map((m) => (
                        <div key={m.month} className="rounded-2xl border border-border/40 bg-muted/20 p-4 transition hover:border-primary/40 hover:bg-primary/5">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{formatMonth(m.month)}</p>
                                    <p className="mt-2 text-2xl font-heading font-bold text-secondary">{safeCurrency(m.net_inr, 'INR')}</p>
                                    <p className="mt-1 text-xs text-muted-foreground">Net payable</p>
                                </div>
                                <Trophy className="h-5 w-5 text-primary" />
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                                <div className="rounded-lg bg-background/60 p-2">
                                    <p className="text-muted-foreground">Streams</p>
                                    <p className="font-mono font-medium">{formatNumber(m.units)}</p>
                                </div>
                                <div className="rounded-lg bg-background/60 p-2">
                                    <p className="text-muted-foreground">Tracks</p>
                                    <p className="font-mono font-medium">{m.track_count}</p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="mt-4 w-full"
                                onClick={() => onMonthDownload(m.month)}
                                disabled={downloadingReport === `monthly_statement_${m.month}`}
                            >
                                {downloadingReport === `monthly_statement_${m.month}`
                                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    : <Download className="mr-2 h-4 w-4" />}
                                Download Statement
                            </Button>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="rounded-xl border border-border/40 bg-muted/20 p-4 text-sm text-muted-foreground">No monthly statements available for this period.</p>
            )}
        </CardContent>
    </Card>
);

const BiggestMovers = ({ movers }) => {
    const isGrowthMode = movers.mode === 'growth';
    return (
        <Card className="border-border/40 bg-card/60">
            <CardHeader>
                <CardTitle>Biggest Movers</CardTitle>
                <CardDescription>{isGrowthMode ? 'Tracks with the strongest movement.' : 'Top revenue contributors while growth data is unavailable.'}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-secondary">{isGrowthMode ? 'Gainers' : 'Top contributors'}</p>
                    {(movers.gainers || []).map((track, index) => <MoverRow key={`${track.title}-g-${index}`} track={track} positive={true} growthMode={isGrowthMode} />)}
                </div>
                <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{isGrowthMode ? 'Decliners' : 'More contributors'}</p>
                    {(movers.decliners || []).map((track, index) => <MoverRow key={`${track.title}-d-${index}`} track={track} positive={isGrowthMode ? track.growth >= 0 : true} growthMode={isGrowthMode} />)}
                </div>
            </CardContent>
        </Card>
    );
};

const MoverRow = ({ track, positive, growthMode }) => (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border/40 bg-muted/20 p-3">
        <div className="min-w-0">
            <p className="truncate font-medium" title={track.title}>{track.title}</p>
            <p className="truncate text-xs text-muted-foreground">{track.artist}</p>
        </div>
        <div className="text-right">
            <p className="font-mono text-sm font-semibold text-secondary">{safeCurrency(track.net_inr, 'INR')}</p>
            {growthMode && <p className={positive ? 'text-xs text-secondary' : 'text-xs text-destructive'}>{positive ? '+' : ''}{Number(track.growth || 0).toFixed(1)}%</p>}
        </div>
    </div>
);

const MiniRank = ({ rank, title, meta, value }) => (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border/40 bg-muted/20 p-3">
        <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{rank}</span>
            <div className="min-w-0">
                <p className="truncate font-medium" title={title}>{title}</p>
                <p className="truncate text-xs text-muted-foreground">{meta}</p>
            </div>
        </div>
        <span className="shrink-0 font-mono text-sm font-semibold text-secondary">{value}</span>
    </div>
);

const DownloadRow = ({ title, description, onClick, downloading }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={downloading}
        className="flex w-full items-center justify-between rounded-xl border border-border/40 bg-muted/20 p-4 text-left transition hover:border-primary/40 hover:bg-primary/5 disabled:opacity-60"
    >
        <span className="flex items-center gap-3">
            <span className="rounded-lg bg-primary/10 p-2 text-primary"><FileSpreadsheet className="h-4 w-4" /></span>
            <span>
                <span className="block font-medium">{title}</span>
                <span className="block text-xs text-muted-foreground">{description}</span>
            </span>
        </span>
        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-muted-foreground" />}
    </button>
);

const DataTable = ({ children }) => (
    <div className="overflow-hidden rounded-xl border border-border/40">
        <Table>{children}</Table>
    </div>
);

const ChartTooltip = ({ active, payload, label: title }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    return (
        <div className="rounded-lg border border-border bg-popover p-3 shadow-xl">
            <p className="mb-1 text-xs text-muted-foreground">{item?.payload?.monthLabel}</p>
            <p className="font-semibold">{title}: {formatCurrencyFull(item.value || 0, 'INR')}</p>
        </div>
    );
};

export default ReportsPage;
